import React from 'react';
import { Card, Alert, Progress, Tag, Space, Typography, Divider, Empty } from 'antd';
import {
  BulbOutlined,
  RocketOutlined,
  WarningOutlined,
  TrophyOutlined,
  LineChartOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';

const { Text, Title, Paragraph } = Typography;

const AIInsightsPanel = ({ insights, type = 'dashboard', specificData = null }) => {
  if (!insights) {
    return (
      <Card 
        title={
          <Space>
            <BulbOutlined style={{ color: '#1890ff' }} />
            <span>AI Insights</span>
          </Space>
        }
        className="h-full"
      >
        <Empty 
          description="No AI insights available"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  const getHealthColor = (score) => {
    if (score >= 80) return '#52c41a';
    if (score >= 60) return '#faad14';
    return '#ff4d4f';
  };

  const getHealthLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Attention';
  };

  return (
    <div style={{ position: 'sticky', top: '20px' }}>
      <Card 
        title={
          <Space>
            <BulbOutlined style={{ color: '#1890ff' }} />
            <span>AI Insights</span>
          </Space>
        }
        className="shadow-lg"
        style={{ borderRadius: '8px' }}
      >
        {/* Summary Section */}
        <Alert
          message="Executive Summary"
          description={insights.summary}
          type="info"
          showIcon
          icon={<BulbOutlined />}
          style={{ marginBottom: '16px' }}
        />

        {/* Specific Data Context */}
        {specificData && (
          <Card size="small" className="mb-4" style={{ backgroundColor: '#f0f5ff' }}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              {specificData.name && (
                <Text strong style={{ fontSize: '16px' }}>{specificData.name}</Text>
              )}
              {specificData.metrics && specificData.metrics.map((metric, idx) => (
                <div key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">{metric.label}:</Text>
                  <Text strong>{metric.value}</Text>
                </div>
              ))}
            </Space>
          </Card>
        )}

        {/* Health Score */}
        {(insights.healthScore || insights.productivityScore || insights.organizationalHealth || insights.budgetHealth) && (
          <div style={{ marginBottom: '20px' }}>
            <Text strong>
              {type === 'employee' ? 'Productivity Score' : 
               type === 'salary' ? 'Budget Health' : 
               type === 'project' ? 'Project Health' : 
               'Organizational Health'}
            </Text>
            <div style={{ marginTop: '8px' }}>
              <Progress
                percent={insights.healthScore || insights.productivityScore || insights.organizationalHealth || insights.budgetHealth}
                strokeColor={getHealthColor(insights.healthScore || insights.productivityScore || insights.organizationalHealth || insights.budgetHealth)}
                format={(percent) => `${percent}% - ${getHealthLabel(percent)}`}
              />
            </div>
          </div>
        )}

        <Divider />

        {/* Key Highlights / Recommendations */}
        {insights.recommendations && insights.recommendations.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <Title level={5}>
              <RocketOutlined style={{ marginRight: '8px', color: '#52c41a' }} />
              Recommendations
            </Title>
            <ul style={{ paddingLeft: '20px', margin: 0 }}>
              {insights.recommendations.map((rec, idx) => (
                <li key={idx} style={{ marginBottom: '10px' }}>
                  <Text>{rec}</Text>
                </li>
              ))}
            </ul>
          </div>
        )}

        {insights.keyHighlights && insights.keyHighlights.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <Title level={5}>
              <TrophyOutlined style={{ marginRight: '8px', color: '#faad14' }} />
              Key Highlights
            </Title>
            <ul style={{ paddingLeft: '20px', margin: 0 }}>
              {insights.keyHighlights.map((highlight, idx) => (
                <li key={idx} style={{ marginBottom: '10px' }}>
                  <Text>{highlight}</Text>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Action Items */}
        {insights.actionItems && insights.actionItems.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <Title level={5}>
              <CheckCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
              Priority Actions
            </Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              {insights.actionItems.map((action, idx) => (
                <Alert
                  key={idx}
                  message={action}
                  type="warning"
                  showIcon
                  style={{ fontSize: '13px' }}
                />
              ))}
            </Space>
          </div>
        )}

        {/* Trends */}
        {insights.trends && insights.trends.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <Title level={5}>
              <LineChartOutlined style={{ marginRight: '8px', color: '#722ed1' }} />
              Trends
            </Title>
            <Space wrap style={{ width: '100%' }}>
              {insights.trends.map((trend, idx) => (
                <Tag 
                  key={idx} 
                  color="blue" 
                  style={{ 
                    padding: '4px 12px', 
                    marginBottom: '8px',
                    maxWidth: '100%',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word'
                  }}
                >
                  {trend}
                </Tag>
              ))}
            </Space>
          </div>
        )}

        {/* Work Pattern */}
        {insights.workPattern && (
          <div style={{ marginBottom: '20px' }}>
            <Title level={5}>Work Pattern</Title>
            <Tag color={
              insights.workPattern.toLowerCase().includes('consistent') ? 'green' :
              insights.workPattern.toLowerCase().includes('irregular') ? 'orange' :
              insights.workPattern.toLowerCase().includes('overworked') ? 'red' : 'blue'
            } style={{ padding: '6px 14px', fontSize: '14px' }}>
              {insights.workPattern}
            </Tag>
          </div>
        )}

        {/* Risk Factors / Concerns */}
        {((insights.riskFactors && insights.riskFactors.length > 0) || 
          (insights.concerns && insights.concerns.length > 0)) && (
          <div>
            <Title level={5}>
              <WarningOutlined style={{ marginRight: '8px', color: '#ff4d4f' }} />
              {insights.riskFactors ? 'Risk Factors' : 'Concerns'}
            </Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              {(insights.riskFactors || insights.concerns).map((item, idx) => (
                <Alert
                  key={idx}
                  message={item}
                  type="error"
                  showIcon
                  style={{ fontSize: '13px' }}
                />
              ))}
            </Space>
          </div>
        )}
      </Card>
    </div>
  );
};

export default AIInsightsPanel;
